// src/pages/user/PackageHistory.js
import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import { 
  Package, 
  Calendar, 
  DollarSign, 
  TrendingUp, 
  Gift, 
  User, 
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  AlertCircle,
  Wallet
} from 'lucide-react';

const PackageHistory = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const storedUserId = localStorage.getItem('userId');
    const storedDbKey = localStorage.getItem('dbKey');
    const storedRegistrationDate = localStorage.getItem('registrationDate') || '2026-02-06';
    
    if (storedUserId) {
      setUserId(storedUserId);
      fetchPackageHistory(storedDbKey || storedUserId, storedRegistrationDate);
    }
  }, []);

  const fetchPackageHistory = async (dbKey, registrationDate) => {
    try {
      setLoading(true);
      
      // Try date-wise structure
      const userRef = ref(database, `users/${registrationDate}/${dbKey}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const userData = snapshot.val();
        const userPackages = userData.packages || [];
        
        // Also fetch packages gifted to others
        const giftedPackages = await fetchGiftedPackages(userData.sponsorId);
        
        // Combine both lists
        const allPackages = [
          ...userPackages.map(pkg => ({
            ...pkg,
            type: 'self',
            purchasedBy: pkg.purchasedBy || 'self'
          })),
          ...giftedPackages.map(pkg => ({
            ...pkg,
            type: 'gifted'
          }))
        ];
        
        // Sort by purchase date
        const sortedPackages = allPackages.sort((a, b) => 
          new Date(b.purchaseDate || b.date) - new Date(a.purchaseDate || a.date)
        );
        
        setPackages(sortedPackages);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGiftedPackages = async (sponsorId) => {
    try {
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      if (!snapshot.exists()) return [];
      
      const usersByDate = snapshot.val();
      const giftedPackages = [];
      
      // Search through all users to find packages gifted by this user
      for (const date in usersByDate) {
        const users = usersByDate[date];
        
        for (const [dbKey, user] of Object.entries(users)) {
          const userPackages = user.packages || [];
          
          userPackages.forEach(pkg => {
            if (pkg.purchasedBy === sponsorId && pkg.purchasedBy !== 'self') {
              giftedPackages.push({
                ...pkg,
                type: 'gifted',
                targetUserName: user.name || 'Unknown',
                targetUserId: user.sponsorId || dbKey,
                targetDbKey: dbKey,
                targetRegistrationDate: date
              });
            }
          });
        }
      }
      
      return giftedPackages;
    } catch (error) {
      console.error('Error fetching gifted packages:', error);
      return [];
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'active': return 'text-green-400 bg-green-500/20';
      case 'completed': return 'text-blue-400 bg-blue-500/20';
      case 'expired': return 'text-red-400 bg-red-500/20';
      default: return 'text-yellow-400 bg-yellow-500/20';
    }
  };

  const getTypeBadge = (pkg) => {
    if (pkg.type === 'gifted') {
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center gap-1">
          <Gift size={12} /> Gifted
        </span>
      );
    } else if (pkg.purchasedBy && pkg.purchasedBy !== 'self') {
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 flex items-center gap-1">
          <User size={12} /> Gifted to You
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
          Self Purchase
        </span>
      );
    }
  };

  const filteredPackages = packages.filter(pkg => {
    if (filter === 'all') return true;
    if (filter === 'active') return pkg.status === 'active';
    if (filter === 'completed') return pkg.status === 'completed';
    if (filter === 'self') return pkg.type === 'self' && pkg.purchasedBy === 'self';
    if (filter === 'gifted') return pkg.type === 'gifted';
    if (filter === 'received') return pkg.purchasedBy && pkg.purchasedBy !== 'self';
    return true;
  });

  const stats = {
    total: packages.length,
    active: packages.filter(p => p.status === 'active').length,
    selfPurchased: packages.filter(p => p.type === 'self' && p.purchasedBy === 'self').length,
    gifted: packages.filter(p => p.type === 'gifted').length,
    received: packages.filter(p => p.purchasedBy && p.purchasedBy !== 'self').length
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pt-20 px-4 pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Package History</h1>
          <p className="text-gray-400 text-sm">View all your package investments</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="bg-gray-800/50 rounded-xl p-3">
            <div className="text-gray-400 text-xs">Total</div>
            <div className="text-xl font-bold text-white">{stats.total}</div>
          </div>
          <div className="bg-green-500/10 rounded-xl p-3">
            <div className="text-green-400 text-xs">Active</div>
            <div className="text-xl font-bold text-green-400">{stats.active}</div>
          </div>
          <div className="bg-blue-500/10 rounded-xl p-3">
            <div className="text-blue-400 text-xs">Self</div>
            <div className="text-xl font-bold text-blue-400">{stats.selfPurchased}</div>
          </div>
          <div className="bg-purple-500/10 rounded-xl p-3">
            <div className="text-purple-400 text-xs">Gifted</div>
            <div className="text-xl font-bold text-purple-400">{stats.gifted}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['all', 'active', 'self', 'gifted', 'received'].map((filterType) => (
            <button
              key={filterType}
              onClick={() => setFilter(filterType)}
              className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm ${
                filter === filterType 
                  ? filterType === 'active' ? 'bg-green-500 text-white' :
                    filterType === 'self' ? 'bg-blue-500 text-white' :
                    filterType === 'gifted' ? 'bg-purple-500 text-white' :
                    filterType === 'received' ? 'bg-yellow-500 text-white' :
                    'bg-blue-500 text-white'
                  : 'bg-gray-800 text-gray-300'
              }`}
            >
              {filterType === 'all' && 'All'}
              {filterType === 'active' && 'Active'}
              {filterType === 'self' && 'Self'}
              {filterType === 'gifted' && 'Gifted'}
              {filterType === 'received' && 'Received'}
            </button>
          ))}
        </div>

        {/* Packages List */}
        <div className="space-y-3">
          {filteredPackages.length === 0 ? (
            <div className="text-center py-12 bg-gray-800/30 rounded-xl">
              <Package className="h-12 w-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">No packages found</p>
              <p className="text-gray-500 text-sm mt-2">Try changing your filter</p>
            </div>
          ) : (
            filteredPackages.map((pkg, index) => (
              <PackageCard key={index} pkg={pkg} index={index} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

// Separate Package Card Component for better organization
const PackageCard = ({ pkg, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'active': return 'text-green-400 bg-green-500/20';
      case 'completed': return 'text-blue-400 bg-blue-500/20';
      case 'expired': return 'text-red-400 bg-red-500/20';
      default: return 'text-yellow-400 bg-yellow-500/20';
    }
  };

  const getTypeBadge = (pkg) => {
    if (pkg.type === 'gifted') {
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center gap-1">
          <Gift size={12} /> Gifted
        </span>
      );
    } else if (pkg.purchasedBy && pkg.purchasedBy !== 'self') {
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 flex items-center gap-1">
          <User size={12} /> Gifted to You
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400 border border-blue-500/30">
          Self Purchase
        </span>
      );
    }
  };

  return (
    <div className="bg-gray-800/50 rounded-xl overflow-hidden">
      {/* Card Header */}
      <div 
        className="p-4 flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${getStatusColor(pkg.status)}`}>
            <Package size={20} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold text-white text-sm">{pkg.packageName || 'Package'}</h3>
              {getTypeBadge(pkg)}
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Calendar size={12} className="text-gray-400" />
              <span className="text-gray-400">{formatDate(pkg.purchaseDate)}</span>
              <span className={`px-2 py-0.5 rounded-full ${getStatusColor(pkg.status)}`}>
                {pkg.status || 'pending'}
              </span>
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-lg font-bold text-white">
            {formatCurrency(pkg.totalAmount || pkg.investmentAmount)}
          </div>
          <button className="text-gray-400 mt-1">
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
      </div>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-700 pt-4">
          {/* Package Type Info */}
          {pkg.type === 'gifted' ? (
            <div className="bg-purple-500/10 p-3 rounded-lg mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Gift size={16} className="text-purple-400" />
                <span className="text-white text-sm font-medium">Gifted To</span>
              </div>
              <p className="text-purple-300 text-sm">
                {pkg.targetUserName} (ID: {pkg.targetUserId})
              </p>
            </div>
          ) : pkg.purchasedBy && pkg.purchasedBy !== 'self' ? (
            <div className="bg-yellow-500/10 p-3 rounded-lg mb-4">
              <div className="flex items-center gap-2 mb-2">
                <User size={16} className="text-yellow-400" />
                <span className="text-white text-sm font-medium">Gifted By</span>
              </div>
              <p className="text-yellow-300 text-sm">From: {pkg.purchasedBy}</p>
            </div>
          ) : null}

          {/* Investment Details */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-gray-900/50 p-3 rounded-lg">
              <div className="text-gray-400 text-xs mb-1">Investment</div>
              <div className="text-white font-medium">
                {formatCurrency(pkg.investmentAmount)}
              </div>
            </div>
            <div className="bg-gray-900/50 p-3 rounded-lg">
              <div className="text-gray-400 text-xs mb-1">Total Value</div>
              <div className="text-green-400 font-medium">
                {formatCurrency(pkg.totalAmount)}
              </div>
            </div>
          </div>

          {/* ROI Details */}
          <div className="bg-blue-500/10 p-4 rounded-lg mb-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp size={16} className="text-blue-400" />
              <span className="text-white text-sm font-medium">ROI Details</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-gray-400 text-xs">Daily ROI</div>
                <div className="text-white font-medium">{pkg.dailyROI || 0}%</div>
              </div>
              <div>
                <div className="text-gray-400 text-xs">Duration</div>
                <div className="text-white font-medium">{pkg.durationDays || 365} days</div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-blue-500/20">
              <div className="text-gray-400 text-xs">Daily Earnings</div>
              <div className="text-green-400 font-medium">
                {formatCurrency(pkg.dailyROIAmount)}
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="space-y-2">
            <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
              <span className="text-gray-400 text-sm">Purchase Date</span>
              <span className="text-white text-sm">{formatDate(pkg.purchaseDate)}</span>
            </div>
            
            {pkg.walletUsed && (
              <div className="flex justify-between items-center py-2 border-b border-gray-700/50">
                <span className="text-gray-400 text-sm">Wallet Used</span>
                <div className="flex items-center gap-1">
                  <Wallet size={12} className="text-gray-400" />
                  <span className="text-white text-sm">{pkg.walletUsed}</span>
                </div>
              </div>
            )}

            {pkg.referralLevels && (
              <div className="bg-green-500/10 p-3 rounded-lg">
                <div className="text-green-400 text-xs mb-1">Commission Levels</div>
                <div className="text-white text-sm">
                  {Array.isArray(pkg.referralLevels) ? pkg.referralLevels.join('/') : '0/0/0/0/0'}%
                </div>
              </div>
            )}

            {pkg.energyPercentage && (
              <div className="bg-yellow-500/10 p-3 rounded-lg">
                <div className="text-yellow-400 text-xs mb-1">Energy Wallet</div>
                <div className="text-white text-sm">{pkg.energyPercentage}% from ROI</div>
              </div>
            )}
          </div>

          {/* Package Sequence */}
          {pkg.packageSequence && (
            <div className="mt-4 pt-3 border-t border-gray-700/50">
              <div className="text-center text-gray-400 text-sm">
                Package #{pkg.packageSequence}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PackageHistory;
